// file: src/components/modals/CreateProjectModal.jsx
import React, { useState, useEffect } from 'react';
import { createProject, getProjectDetail, updateProjectDetail, renameProject } from '../../services/projectServices';
import { useProjectsData } from '../../hooks/useProjectsData';
import { FolderPlus, Edit, AlertTriangle, X, Save } from 'lucide-react';
import styles from './CreateProjectModal.module.css';

function CreateProjectModal({ 
  // Project editing props
  projectId, 
  projectName,
  // Common props 
  onClose, 
  // Creation callback
  onProjectCreated,
  // Edit callbacks 
  onProjectUpdated,
  onProjectRenamed
}) {
  // Determine if we're in edit mode or create mode
  const isEditMode = !!projectId;
  
  // Get existing projects for duplicate name checking
  const { projects } = useProjectsData();
  
  // Common state
  const [name, setName] = useState(projectName || '');
  const [description, setDescription] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [nameWarning, setNameWarning] = useState('');
  
  // Create mode state
  const [isCreating, setIsCreating] = useState(false);
  
  // Edit mode state
  const [isLoading, setIsLoading] = useState(isEditMode);
  const [isSaving, setIsSaving] = useState(false);
  // Start in editing mode when accessed via single-click in LeftSidebar
  const [isEditing, setIsEditing] = useState(true);

  // Check for duplicate names as user types (for create mode)
  useEffect(() => {
    if (isEditMode) return; // Don't check duplicates in edit mode
    
    if (!name.trim()) {
      setNameWarning('');
      return;
    }
    
    // Check if a project with this name already exists
    const existingProject = projects.find(
      project => project.name.toLowerCase() === name.trim().toLowerCase()
    );
    
    if (existingProject) {
      setNameWarning('A project with this name already exists');
    } else {
      setNameWarning('');
    }
  }, [name, projects, isEditMode]);

  // Check for duplicate names in edit mode (excluding current project)
  useEffect(() => {
    if (!isEditMode) return;
    
    if (!name.trim() || name === projectName) {
      setNameWarning('');
      return;
    }
    
    // Check if another project with this name exists (excluding current project)
    const existingProject = projects.find(
      project => project.name.toLowerCase() === name.trim().toLowerCase() && project.id !== projectId
    );
    
    if (existingProject) {
      setNameWarning('Another project with this name already exists');
    } else {
      setNameWarning('');
    }
  }, [name, projects, isEditMode, projectId, projectName]);

  // Load project details when in edit mode
  useEffect(() => {
    async function loadProjectDetails() {
      if (!projectId) return;
      
      setIsLoading(true);
      try {
        const projectDetails = await getProjectDetail(projectId);
        setName(projectDetails.name || '');
        setDescription(projectDetails.description || '');
      } catch (err) {
        setErrorMessage(`Failed to load project details: ${err.message}`);
      } finally {
        setIsLoading(false);
      }
    }
    
    if (isEditMode) {
      loadProjectDetails();
    }
  }, [projectId, isEditMode]);

  // Handle creating a new project
  const handleCreate = async () => {
    if (!name.trim()) {
      setErrorMessage('Project name cannot be empty');
      return;
    }
    
    // Check for frontend validation warning
    if (nameWarning) {
      setErrorMessage(nameWarning);
      return;
    }
    
    setIsCreating(true);
    setErrorMessage('');
    
    try {
      const resp = await createProject(name.trim(), description);
      if (onProjectCreated) {
        onProjectCreated(resp.project_id);
      }
      onClose();
    } catch (err) {
      setErrorMessage(`Failed to create project: ${err.message}`);
      setIsCreating(false);
    }
  };

  // Handle saving changes to an existing project
  const handleSave = async () => {
    if (!projectId) return;
    
    if (!name.trim()) {
      setErrorMessage('Project name cannot be empty');
      return;
    }
    
    // Check for frontend validation warning
    if (nameWarning) {
      setErrorMessage(nameWarning);
      return;
    }
    
    setIsSaving(true);
    setErrorMessage('');
    
    try {
      // If name changed, rename the project
      if (name !== projectName && name.trim()) {
        const renameSuccess = await renameProject(projectId, name.trim());
        if (!renameSuccess) {
          throw new Error('Failed to rename project');
        }
        if (onProjectRenamed) {
          onProjectRenamed(projectId, name.trim());
        }
      }
      
      // Update description
      const success = await updateProjectDetail(projectId, description);
      if (!success) {
        throw new Error('Failed to update project details');
      }
      
      if (onProjectUpdated) {
        onProjectUpdated(projectId, { name: name.trim(), description });
      }
      
      onClose();
    } catch (err) {
      setErrorMessage(`Failed to save changes: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  // Common key handler for both modes
  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.ctrlKey) {
      e.preventDefault();
      if (isEditMode) {
        if (isEditing) handleSave();
      } else {
        handleCreate();
      }
    }
  };

  // Get the appropriate title based on mode
  const getTitle = () => {
    if (isEditMode) {
      return 'Edit Project';
    }
    return 'Create New Project';
  };

  // Get the appropriate info text based on mode
  const getInfoText = () => {
    if (isEditMode) {
      return 'Edit the project name and description';
    }
    return 'Enter a name and optional description for your new design project';
  };

  // Determine if inputs should be disabled
  const areInputsDisabled = () => {
    if (isEditMode) {
      return isSaving || isLoading;
    }
    return isCreating;
  };

  const hasNameWarning = !!nameWarning;

  return (
    <div className={styles.overlay}>
      <div className={styles.modalContent}>
        <div className={styles.header}>
          <div className="flex items-center mb-3">
            <div className={styles.headerIcon}>
              {isEditMode ? <Edit className="h-4 w-4" /> : <FolderPlus className="h-4 w-4" />}
            </div>
            <h2 className={styles.title}>{getTitle()}</h2>
          </div>
          <p className={styles.subtitle}>{getInfoText()}</p>
        </div>

        <div className={styles.content}>
          {isLoading ? (
            <div className={styles.loadingContainer}>
              <div className={styles.spinner}></div>
              <p>Loading project details...</p>
            </div>
          ) : (
            <>
              {/* Project Name Input */}
              <div className={styles.inputGroup}>
                <label htmlFor="project-name" className={styles.label}>
                  Project Name *
                </label>
                <input
                  id="project-name"
                  type="text"
                  placeholder="Enter project name"
                  className={`${styles.input} ${nameWarning ? styles.inputWarning : ''}`}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={areInputsDisabled()}
                  maxLength={100}
                />
                {nameWarning && (
                  <div className={styles.warningMessage}>
                    <AlertTriangle className="h-4 w-4" />
                    {nameWarning}
                  </div>
                )}
              </div>

              {/* Project Description Input */}
              <div className={styles.inputGroup}>
                <label htmlFor="project-description" className={styles.label}>
                  Description
                </label>
                <textarea
                  id="project-description"
                  placeholder="Optional project description"
                  className={styles.textarea}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  disabled={areInputsDisabled()}
                  rows={3}
                  maxLength={500}
                />
              </div>

              {/* Error Message */}
              {errorMessage && (
                <div className={styles.errorMessage}>
                  <AlertTriangle className="h-4 w-4 mr-2" />
                  {errorMessage}
                </div>
              )}
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            onClick={onClose}
            disabled={isCreating || isSaving}
            className={styles.cancelButton}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={isEditMode ? handleSave : handleCreate}
            disabled={isCreating || isSaving || hasNameWarning || !name.trim()}
            className={styles.createButton}
          >
            {(isCreating || isSaving) && (
              <div className={styles.buttonSpinner}></div>
            )}
            {isEditMode ? (
              <>
                <Save className="h-4 w-4" />
                {isSaving ? 'Saving...' : 'Save'}
              </>
            ) : (
              <>
                <FolderPlus className="h-4 w-4" />
                {isCreating ? 'Creating...' : 'Create Project'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default CreateProjectModal;
